<template>
  <div>
    <slot name="trigger" :open="open" :close="close" :toggle="toggle" :isOpen="isOpen" />

    <div
      v-if="isOpen"
      class="modal-overlay def-modal-overlay"
      role="presentation"
      @click.self="onOverlayClick"
    >
      <div
        ref="dialog"
        class="def-modal modal-dialog"
        role="dialog"
        :aria-modal="true"
        :aria-labelledby="computedAriaLabelledby"
        :aria-describedby="computedAriaDescribedby"
        :aria-label="ariaLabel || null"
        tabindex="-1"
      >
        <header v-if="hasHeader" class="modal-header">
          <component
            :is="titleTag"
            v-if="title"
            :id="titleId"
            class="modal-header-title"
          >
            {{ title }}
          </component>

          <slot name="header" />

          <button
            v-if="showCloseButton"
            ref="closeBtn"
            class="modal-close"
            type="button"
            :aria-label="closeAriaLabel"
            @click="requestClose('button')"
          >
            ×
          </button>
        </header>

        <section :id="contentId" class="modal-content">
          <slot />
        </section>

        <footer v-if="$slots.footer" class="modal-footer">
          <slot name="footer" />
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function uniqueId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

export default {
  name: 'Modal',
  model: { prop: 'value', event: 'input' },
  props: {
    // If provided (v-model), the modal is "controlled".
    // If omitted, the modal manages its own open state and can be controlled via ref methods.
    value: { type: Boolean, required: false, default: undefined },

    // Use either `title` (preferred) or `ariaLabel`.
    title: { type: String, default: '' },
    titleTag: { type: String, default: 'h2' },

    // If you can't render a visible title, provide an accessible name.
    ariaLabel: { type: String, default: '' },

    // Optional: let consumers wire their own ids (e.g. for complex layouts)
    ariaLabelledby: { type: String, default: '' },
    ariaDescribedby: { type: String, default: '' },

    closeOnEsc: { type: Boolean, default: true },
    closeOnOverlayClick: { type: Boolean, default: true },
    showCloseButton: { type: Boolean, default: true },

    // If false, the modal will not install document-level keydown/focusin handlers.
    // Useful when other overlays (e.g. vue-loading-overlay) manage focus and would
    // otherwise create a focus loop.
    trapFocus: { type: Boolean, default: true },

    // If false, focus will land on the dialog container itself.
    autoFocus: { type: Boolean, default: true },

    closeAriaLabel: { type: String, default: 'Close dialog' },
  },
  data() {
    return {
      internalOpen: false,
      titleId: uniqueId('modal-header-title'),
      contentId: uniqueId('modal-content'),
      previouslyFocusedEl: null,
      previousBodyOverflow: null,
      documentKeydownHandler: null,
      documentFocusinHandler: null,
    };
  },
  computed: {
    isControlled() {
      // In Vue 2 a prop with default `undefined` will still be present on `this.$props`.
      // Treat it as controlled only when the parent actually binds it.
      return this.$options.propsData && Object.prototype.hasOwnProperty.call(this.$options.propsData, 'value');
    },
    isOpen() {
      return this.isControlled ? this.value : this.internalOpen;
    },
    hasHeader() {
      return Boolean(this.title) || Boolean(this.$slots.header) || this.showCloseButton;
    },
    computedAriaLabelledby() {
      if (this.ariaLabelledby) return this.ariaLabelledby;
      if (this.title) return this.titleId;
      return null;
    },
    computedAriaDescribedby() {
      if (this.ariaDescribedby) return this.ariaDescribedby;
      // Only point to content if there is default slot content.
      return this.$slots.default ? this.contentId : null;
    },
  },
  watch: {
    value(isOpen) {
      // Only react to v-model changes in controlled mode.
      if (!this.isControlled) return;
      if (isOpen) this.$nextTick(this.onOpen);
      else this.onClose();
    },
  },
  mounted() {
    if (this.isOpen) this.$nextTick(this.onOpen);
  },
  beforeDestroy() {
    // Ensure cleanup even if component is destroyed while open.
    this.onClose();
  },
  methods: {
    open(payload) {
      // Payload is forwarded to @shown.
      if (this.isControlled) this.$emit('input', true);
      else this.internalOpen = true;
      this.$nextTick(() => {
        // onOpen will run via watcher (controlled) or not (uncontrolled). Ensure it runs in both cases.
        if (this.isControlled) return;
        this.onOpen(payload);
      });
    },
    close(trigger = 'api') {
      this.requestClose(trigger);
    },
    toggle() {
      if (this.isOpen) this.close('api');
      else this.open();
    },
    requestClose(trigger) {
      this.$emit('close', { trigger });
      if (this.isControlled) this.$emit('input', false);
      else this.internalOpen = false;
    },
    onOverlayClick() {
      if (this.closeOnOverlayClick) this.requestClose('overlay');
    },
    onOpen() {
      if (typeof document === 'undefined') return;

      this.previouslyFocusedEl = document.activeElement;

      // Lock background scroll (simple + robust)
      if (document.body) {
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      }

      if (this.trapFocus) {
        // Strong focus trap: handle Tab/ESC at the document level so focus can't escape.
        // Use capture=true so we run before page handlers.
        if (!this.documentKeydownHandler) {
          this.documentKeydownHandler = (e) => {
            if (!this.isOpen) return;
            if (!e) return;

            if (e.key === 'Tab') {
              this.handleTrapFocus(e);
            } else if ((e.key === 'Escape' || e.key === 'Esc') && this.closeOnEsc) {
              e.preventDefault();
              this.requestClose('esc');
            }
          };
          document.addEventListener('keydown', this.documentKeydownHandler, true);
        }

        // If anything outside the dialog receives focus (mouse, AT, programmatic), pull it back in.
        if (!this.documentFocusinHandler) {
          this.documentFocusinHandler = (e) => {
            if (!this.isOpen) return;
            const dlg = this.$refs.dialog;
            if (!dlg) return;

            const target = e && e.target;
            if (target && (target === dlg || dlg.contains(target))) return;

            const focusables = this.getFocusableElements();
            const fallback = focusables[0] || dlg;
            if (fallback && typeof fallback.focus === 'function') {
              try {
                fallback.focus();
              } catch (err) {
                // ignore
              }
            }
          };
          document.addEventListener('focusin', this.documentFocusinHandler, true);
        }
      }

      this.$nextTick(() => {
        if (!this.isOpen) return;

        if (this.autoFocus) {
          const closeBtn = this.$refs.closeBtn;
          if (closeBtn && typeof closeBtn.focus === 'function') {
            closeBtn.focus();
            return;
          }
          if (this.focusFirstFocusable()) return;
        }

        const dlg = this.$refs.dialog;
        if (dlg && typeof dlg.focus === 'function') dlg.focus();

        // Modal is now interactive (focus trap + scroll lock are active)
        this.$emit('shown');
      });
    },

    onClose() {
      if (typeof document !== 'undefined') {
        if (this.documentKeydownHandler) {
          document.removeEventListener('keydown', this.documentKeydownHandler, true);
          this.documentKeydownHandler = null;
        }
        if (this.documentFocusinHandler) {
          document.removeEventListener('focusin', this.documentFocusinHandler, true);
          this.documentFocusinHandler = null;
        }

        if (document.body) {
          document.body.style.overflow = this.previousBodyOverflow == null ? '' : this.previousBodyOverflow;
        }
      }
      this.previousBodyOverflow = null;

      const el = this.previouslyFocusedEl;
      this.previouslyFocusedEl = null;
      if (el && typeof el.focus === 'function') {
        if (el.isConnected === false) return;
        try {
          el.focus();
        } catch (err) {
          // ignore focus errors
        }
      }

      this.$emit('hidden');
    },

    getFocusableElements() {
      const dlg = this.$refs.dialog;
      if (!dlg) return [];
      const nodes = Array.prototype.slice.call(dlg.querySelectorAll(FOCUSABLE_SELECTOR));
      // filter out elements that are not actually visible
      return nodes.filter((n) => {
        if (!n) return false;
        // offsetParent is null for display:none; allow fixed/positioned too via getClientRects
        return n.offsetWidth > 0 || n.offsetHeight > 0 || (n.getClientRects && n.getClientRects().length > 0);
      });
    },
    focusFirstFocusable() {
      const list = this.getFocusableElements();
      if (!list.length) return false;
      list[0].focus();
      return true;
    },
    handleTrapFocus(e) {
      const dlg = this.$refs.dialog;
      if (!dlg) return;

      const focusables = this.getFocusableElements();
      if (!focusables.length) {
        // Keep focus on the dialog container if there's nothing focusable
        e.preventDefault();
        if (typeof dlg.focus === 'function') dlg.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = typeof document !== 'undefined' ? document.activeElement : null;

      const activeIsInside = active && (active === dlg || dlg.contains(active));
      if (!activeIsInside) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }

      if (e.shiftKey) {
        if (active === first || active === dlg) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 200000;
  display: flex;
  justify-content: center;
  padding: 24px;
  /* Overlay should capture outside clicks, but never block clicks inside the dialog */
  pointer-events: auto;
}

.modal-dialog {
  position: relative;
  z-index: 1;
  pointer-events: auto;
  width: 100%;
  max-width: 900px;
  max-height: fit-content;
  overflow: hidden; /* scroll is handled by the content area */
  outline: none;
  display: flex;
  flex-direction: column;
}

.modal-header,
.modal-footer {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  border: none;
}

.modal-header-title {
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.3;
}

.modal-close {
  margin-left: auto;
  font-size: 1.5rem;
  line-height: 1;
  padding: 6px 10px;
  cursor: pointer;
}

.modal-content {
  overflow: auto;
  flex: 1 1 auto;
  min-height: 0; /* required for flex children to be scrollable */
  border: none;
}

@media (prefers-reduced-motion: reduce) {
  * {
    scroll-behavior: auto !important;
    transition: none !important;
    animation: none !important;
  }
}
</style>

